import { useEffect } from "react";
import { AlertTriangle, X } from "lucide-react";

/**
 * ConfirmDialog — reusable modal replacement for `window.confirm()`.
 * Handles: click-outside dismiss, Escape key, focus trap on Confirm button,
 * body scroll lock while open. Works reliably in preview environments where
 * `window.confirm()` may be auto-dismissed by the browser or dev tooling.
 *
 * Usage:
 *   const [dlg, setDlg] = useState(null);
 *   ...
 *   <button onClick={() => setDlg({ title:'Delete?', body:'...', onConfirm: async ()=>{...} })}>Delete</button>
 *   <ConfirmDialog dialog={dlg} onClose={() => setDlg(null)} />
 */
export default function ConfirmDialog({ dialog, onClose }) {
  const open = !!dialog;

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open) return null;

  const {
    title = "Are you sure?",
    body = "This action cannot be undone.",
    confirmLabel = "Delete",
    cancelLabel = "Cancel",
    danger = true,
    onConfirm,
  } = dialog;

  const handleConfirm = async () => {
    try { await onConfirm?.(); }
    finally { onClose(); }
  };

  return (
    <div
      data-testid="confirm-dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-dialog-title"
      className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-2xl max-w-md w-full overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 pt-5 pb-3 flex items-start gap-3">
          <div className={`rounded-full p-2 ${danger ? "bg-red-50" : "bg-blue-50"}`}>
            <AlertTriangle size={20} className={danger ? "text-red-600" : "text-blue-600"} />
          </div>
          <div className="flex-1 min-w-0">
            <h2 id="confirm-dialog-title" className="font-display font-semibold text-slate-900 text-lg">{title}</h2>
            <p className="mt-1 text-sm text-slate-600 whitespace-pre-wrap">{body}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="confirm-dialog-close"
            className="p-1 text-slate-400 hover:text-slate-700 rounded"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>
        <div className="px-5 py-3 bg-slate-50 border-t border-slate-100 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            data-testid="confirm-dialog-cancel"
            className="px-4 py-2 text-sm border border-slate-300 text-slate-700 rounded-md hover:bg-white font-medium"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            autoFocus
            data-testid="confirm-dialog-confirm"
            className={`px-4 py-2 text-sm text-white rounded-md font-medium ${
              danger ? "bg-red-600 hover:bg-red-700" : "bg-[#0F4C81] hover:bg-[#0A365C]"
            }`}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
